import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  clearAdminKey,
  getAdminKey,
  getProfile,
  loginWithPassword as requestPasswordLogin,
  opsFetch,
  setAdminKey,
  setProfile,
  type OpsProfile,
} from "../lib/api";

type AuthCtx = {
  isAuthenticated: boolean;
  profile: OpsProfile | null;
  login: (key: string) => Promise<void>;
  loginWithPassword: (username: string, password: string) => Promise<void>;
  logout: () => void;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authed, setAuthed] = useState(() => !!getAdminKey());
  const [profile, setProfileState] = useState<OpsProfile | null>(() => getProfile());

  const login = useCallback(async (key: string) => {
    setAdminKey(key);
    try {
      const me = await opsFetch<{ data: OpsProfile }>("/api/ops/me");
      setProfile(me.data);
      setProfileState(me.data);
      setAuthed(true);
    } catch (err) {
      clearAdminKey();
      throw err;
    }
  }, []);

  const loginWithPassword = useCallback(async (username: string, password: string) => {
    const result = await requestPasswordLogin(username, password);
    setAdminKey(result.token);
    setProfile(result.profile);
    setProfileState(result.profile);
    setAuthed(true);
  }, []);

  const logout = useCallback(() => {
    clearAdminKey();
    setProfileState(null);
    setAuthed(false);
  }, []);

  const value = useMemo(
    () => ({ isAuthenticated: authed, profile, login, loginWithPassword, logout }),
    [authed, profile, login, loginWithPassword, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
